import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import RouterWrapper from './components/RouterWrapper';
import ScrollToTop from './components/ScrollToTop';
import GlobalStyles from './styles/GlobalStyles';
import { I18nProvider, LOCALES, localeFromPathname, localePath, preferredLocale } from './i18n';
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import SolutionsPage from './pages/SolutionsPage';
import AboutUsPage from './pages/AboutUsPage';
import ProductPage from './pages/ProductPage';
import ContactPage from './pages/ContactPage';
import PortfoliosPage from './pages/PortfoliosPage';
import BlogPage from './pages/blog';
import BlogDetailPage from './pages/blog/[slug]';
import SolutionDetailPage from './pages/SolutionDetailPage';
import DownloadsPage from './pages/DownloadsPage';
import DemoPage from './pages/DemoPage';
import HRMSPricePage from './pages/HRMSPricePage';
import FeaturesPage from './pages/FeaturesPage';
import NotFoundPage from './pages/NotFoundPage';

/** Redirect to a locale-prefixed path from inside a locale route tree. */
const LocaleRedirect: React.FC<{ to: string }> = ({ to }) => {
  const location = useLocation();
  const locale = localeFromPathname(location.pathname);
  return <Navigate to={localePath(locale, to)} replace />;
};

/** Anything outside /en or /km is redirected to the visitor's best locale. */
const DefaultLocaleRedirect: React.FC = () => {
  const location = useLocation();
  const locale = preferredLocale();
  return <Navigate to={localePath(locale, location.pathname)} replace />;
};

interface PageRouteConfig {
  path?: string;
  element: React.ReactNode;
}

/**
 * All page routes, relative to their locale prefix (e.g. /en/about-us).
 * Rendered once per locale below.
 */
const PAGE_ROUTES: PageRouteConfig[] = [
  { element: <HomePage /> },
  { path: 'home', element: <LocaleRedirect to="/" /> },
  { path: 'index.html', element: <LocaleRedirect to="/" /> },
  { path: 'about', element: <LocaleRedirect to="/about-us" /> },
  { path: 'about-us', element: <AboutUsPage /> },
  { path: 'solutions', element: <SolutionsPage /> },
  { path: 'features', element: <FeaturesPage /> },
  { path: 'pricing', element: <LocaleRedirect to="/products/hrms" /> },
  { path: 'downloads', element: <DownloadsPage /> },
  { path: 'products/hrms-price', element: <HRMSPricePage /> },
  { path: 'products/:productSlug', element: <ProductPage /> },
  { path: 'solutions/:solutionSlug', element: <SolutionDetailPage /> },
  { path: 'portfolios', element: <PortfoliosPage /> },
  { path: 'contact', element: <ContactPage /> },
  { path: 'blog', element: <BlogPage /> },
  { path: 'blog/:slug', element: <BlogDetailPage /> },
  { path: 'demo', element: <DemoPage /> },
  { path: '*', element: <NotFoundPage /> },
];

const App: React.FC = () => {
  return (
    <RouterWrapper>
      <I18nProvider>
        <HelmetProvider>
          <ScrollToTop />
          <GlobalStyles />
          <div className="App">
            <Header />
            <main>
              <Routes>
                {LOCALES.map((locale) => (
                  <Route key={locale} path={locale}>
                    {PAGE_ROUTES.map((route, index) =>
                      route.path ? (
                        <Route key={route.path} path={route.path} element={route.element} />
                      ) : (
                        <Route key={`index-${index}`} index element={route.element} />
                      )
                    )}
                  </Route>
                ))}
                <Route path="*" element={<DefaultLocaleRedirect />} />
              </Routes>
            </main>
            <Footer />
          </div>
        </HelmetProvider>
      </I18nProvider>
    </RouterWrapper>
  );
};

export default App;
